'use client';

import * as React from 'react';
import {
  AccordionItem as ShadcnAccordionItem,
  AccordionTrigger as ShadcnAccordionTrigger,
  AccordionContent as ShadcnAccordionContent,
  Accordion as ShadcnAccordion,
} from '@/hen-ui/shadcn/accordion';
import { cn } from '@/hen-ui/utils';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Heading } from '@/hen-ui/atomics/Heading';
import {
  accordionVariants,
  type AccordionVariantProps,
} from './accordion.variants';

// Base props shared by all accordion types
interface BaseAccordionProps extends AccordionVariantProps {
  items: {
    title: string;
    content: React.ReactNode;
    icon?: React.ComponentProps<typeof Icon>['icon'];
    iconBg?: boolean;
    value?: string;
  }[];
}

// Single accordion can have collapsible
interface SingleAccordionProps extends BaseAccordionProps {
  type?: 'single';
  collapsible?: boolean;
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
}

// Multiple accordion cannot have collapsible
interface MultipleAccordionProps extends BaseAccordionProps {
  type: 'multiple';
  collapsible?: never;
  defaultValue?: string[];
  value?: string[];
  onValueChange?: (value: string[]) => void;
}

// Discriminated union
export type AccordionProps = SingleAccordionProps | MultipleAccordionProps;

export const Accordion = React.forwardRef<
  React.ElementRef<typeof ShadcnAccordion>,
  AccordionProps
>((props, ref) => {
  const { items, variant } = props;
  const type = props.type ?? 'single';

  const showIconBg = (iconBg?: boolean) => iconBg ?? true;

  // Type-safe props for shadcn accordion
  const accordionProps =
    type === 'single'
      ? {
          type: 'single' as const,
          collapsible: props.collapsible ?? true,
          defaultValue: props.defaultValue as string | undefined,
          value: props.value as string | undefined,
          onValueChange: props.onValueChange as
            | ((value: string) => void)
            | undefined,
        }
      : {
          type: 'multiple' as const,
          defaultValue: props.defaultValue as string[] | undefined,
          value: props.value as string[] | undefined,
          onValueChange: props.onValueChange as
            | ((value: string[]) => void)
            | undefined,
        };

  return (
    <ShadcnAccordion ref={ref} {...accordionProps}>
      {items.map((item, index) => {
        return (
          <ShadcnAccordionItem
            value={item.value ?? `item-${index}`}
            key={index}
            className={cn(accordionVariants({ variant }))}
          >
            <ShadcnAccordionTrigger>
              <Heading variant='h5' className='flex items-center gap-2'>
                {item.icon && (
                  <Icon
                    icon={item.icon}
                    size='sm'
                    color='black'
                    background={
                      showIconBg(item.iconBg)
                        ? variant === 'primary'
                          ? 'primary'
                          : variant === 'secondary'
                            ? 'secondary'
                            : 'neutral'
                        : undefined
                    }
                  />
                )}
                {item.title}
              </Heading>
            </ShadcnAccordionTrigger>
            <ShadcnAccordionContent>{item.content}</ShadcnAccordionContent>
          </ShadcnAccordionItem>
        );
      })}
    </ShadcnAccordion>
  );
});

Accordion.displayName = 'Accordion';
